import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useToast } from '../context/ToastContext';
import { useLoading } from '../hooks/useLoading';
import { Topbar } from '../components/Topbar';
import { 
  GraduationCap, CheckCircle, Info, User, 
  Phone, Fingerprint, XCircle, MessageCircle,
  LayoutGrid, AlertCircle, Camera, Lock, Shield,
  Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Badge, SearchInput, Skeleton, Input } from '../components/ui';

export function CoursesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { processEnrollment, allEnrollments, removeEnrollment, courses } = useData();
  const { addToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const loading = useLoading(500);

  // Form State
  const [showForm, setShowForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    citizenId: '',
    phone: '',
    password: '',
    confirmPassword: '',
    profilePicture: ''
  });

  // Success State
  const [enrollmentResult, setEnrollmentResult] = useState(null);

  // ... (restante do código até o render)

  const handleCancelEnrollment = () => {
    if (!userEnrollment) return;
    const courseDisplay = Array.isArray(userEnrollment.courses) && userEnrollment.courses.length > 0
      ? userEnrollment.courses.join(', ')
      : (userEnrollment.course || userEnrollment.courseName);
    const confirmed = window.confirm(`Tem certeza que deseja cancelar sua matrícula em ${courseDisplay}? Esta ação é irreversível.`);
    
    if (confirmed) {
      setIsCancelling(true);
      const result = removeEnrollment(userEnrollment.enrollmentId);
      if (result.success) {
        addToast('Sua matrícula foi cancelada. Você agora pode se inscrever em um novo curso.', 'success');
      } else {
        addToast('Erro ao cancelar matrícula.', 'error');
      }
      setIsCancelling(false);
    }
  };

  // ... (restante do código até o return do card)

  // Filtrar apenas cursos ativos
  const filteredCourses = useMemo(() => {
    return (courses || [])
      .filter(c => c.status === 'Ativo')
      .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, courses]);

  // Verificar se o usuário logado já possui QUALQUER matrícula
  const userEnrollment = useMemo(() => {
    return (allEnrollments || []).find(e => 
      e.enrollmentId === user?.enrollmentId || e.citizenId === user?.citizenId
    );
  }, [allEnrollments, user]);

  const handleEnrollClick = (course) => {
    // Proteção: Se não houver curso ou dados básicos
    if (!course || !course.name) {
      addToast('Ocorreu um problema ao carregar as informações do curso.', 'error');
      return;
    }

    if (userEnrollment) {
      addToast(`Matrícula indisponível. Você já possui matrícula ativa em ${userEnrollment.course || userEnrollment.courseName}.`, 'error');
      return;
    }

    const totalVacancies = course.turmas.reduce((sum, t) => sum + t.vacancies, 0);
    const studentsInCourse = (allEnrollments || []).filter(e => e.course === course.name || e.courseName === course.name);
    if (studentsInCourse.length >= totalVacancies) {
      addToast('Curso lotado. Todas as vagas das turmas disponíveis já foram preenchidas.', 'error');
      return;
    }
    
    setSelectedCourse(course);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      addToast('As senhas não coincidem.', 'error');
      return;
    }

    const result = await processEnrollment({
      ...formData,
      courseName: selectedCourse.name
    });

    if (result.success) {
      setEnrollmentResult(result.data);
      setShowForm(false);
      setFormData({ 
        fullName: '', 
        citizenId: '', 
        phone: '', 
        password: '',
        confirmPassword: ''
      });
      addToast('Matrícula realizada com sucesso!', 'success');
    } else {
      addToast(result.message, 'error');
    }
  };

  // Se houver erro crítico na renderização do componente principal
  if (!courses || courses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#070d19] p-6 text-center">
        <div className="w-20 h-20 bg-red/10 rounded-full flex items-center justify-center mb-6 text-red">
          <AlertCircle size={40} />
        </div>
        <h2 className="text-2xl font-black text-white mb-2">Erro de Configuração</h2>
        <p className="text-muted mb-8 max-w-xs">Não foi possível carregar o catálogo de cursos institucional.</p>
        <Button className="px-8 py-3 rounded-2xl font-black" onClick={() => window.location.reload()}>
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Topbar />
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-purple">
            <LayoutGrid size={24} />
            <span className="text-xs font-black uppercase tracking-[0.2em]">Catálogo Institucional</span>
          </div>
          <h2 className="text-5xl font-black tracking-tighter">Cursos / Matérias</h2>
          <p className="text-muted text-lg font-medium max-w-xl">
            Visualize todos os cursos disponíveis, acompanhe a disponibilidade de vagas em tempo real e inicie sua jornada acadêmica.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
          <div className="relative group w-full sm:w-80">
            <SearchInput 
              value={searchTerm} 
              onChange={setSearchTerm} 
              placeholder="Pesquisar graduação..."
              className="w-full bg-[#0a1120] border-[#1d2a42] h-14"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
        {loading ? (
          [1, 2, 3, 4].map(i => <Skeleton key={i} className="h-[580px] rounded-[3rem]" />)
        ) : (
          (filteredCourses || []).map((course) => {
            // Proteção de dados do curso
            if (!course) return null;

            const studentsInCourse = (allEnrollments || []).filter(e => {
              const sc = Array.isArray(e.courses) && e.courses.length > 0
                ? e.courses : [e.course || e.courseName].filter(Boolean);
              return sc.includes(course.name);
            });
            
            const totalVacancies = course.turmas.reduce((sum, t) => sum + t.vacancies, 0);
            const totalOccupied = studentsInCourse.length;
            const remainingVacancies = totalVacancies - totalOccupied;
            const occupancyRate = totalVacancies > 0 ? (totalOccupied / totalVacancies) * 100 : 0;
            
            const isFull = totalOccupied >= totalVacancies;
            // Verifica se o aluno está matriculado NESTE curso específico
            const isStudentInThisCourse = userEnrollment && (
              (Array.isArray(userEnrollment.courses) && userEnrollment.courses.includes(course.name)) ||
              userEnrollment.course === course.name ||
              userEnrollment.courseName === course.name
            );

            // Status Logic
            let statusLabel = "Vagas Disponíveis";
            let statusColor = "green";
            let statusIcon = "🟢";

            if (isFull) {
              statusLabel = "Curso Lotado";
              statusColor = "red";
              statusIcon = "🔴";
            } else if (remainingVacancies <= 5) {
              statusLabel = "Últimas Vagas";
              statusColor = "yellow";
              statusIcon = "🟡";
            }

            return (
              <article 
                key={course.id || course.name} 
                className={`relative flex flex-col bg-[#0a1120] border border-[#1d2a42] rounded-[3rem] p-10 transition-all duration-500 hover:border-purple/30 group
                  ${isStudentInThisCourse ? 'shadow-[0_0_60px_-15px_rgba(168,85,247,0.3)] border-purple/60' : 'hover:shadow-2xl hover:shadow-black/40'}
                `}
              >
                {/* Header do Card */}
                <div className="flex items-start justify-between mb-10">
                  <div className={`w-16 h-16 rounded-3xl flex items-center justify-center transition-all duration-500 shadow-inner
                    ${isStudentInThisCourse ? 'bg-purple text-white' : 'bg-purple/10 text-purple group-hover:scale-110'}
                  `}>
                    <GraduationCap size={32}/>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant={statusColor} className="px-6 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest border-none shadow-sm">
                      {statusIcon} {statusLabel}
                    </Badge>
                  </div>
                </div>

                {/* Conteúdo Principal */}
                <div className="mb-10">
                  <h3 className="text-3xl font-black mb-4 tracking-tighter uppercase group-hover:text-purple transition-colors">{course.name || "Curso Sem Nome"}</h3>
                  <p className="text-muted text-base leading-relaxed font-medium">
                    {course.description || "Formação acadêmica de excelência focada em preparar profissionais capacitados para os desafios do mercado de trabalho global."}
                  </p>
                </div>

                {/* Indicadores de Turmas */}
                <div className="grid grid-cols-1 gap-4 mb-8">
                  {course.turmas.map((turma) => {
                    const occupied = studentsInCourse.filter(e => e.turma === turma.turmaId).length;
                    return (
                      <div key={turma.turmaId} className="flex justify-between items-center bg-[#070d19] p-5 rounded-2xl border border-[#1d2a42]">
                        <span className="text-[10px] uppercase text-muted font-black tracking-widest">Turma {turma.turmaId}</span>
                        <strong className="text-white font-black">{occupied} / {turma.vacancies} <span className="text-[10px] text-muted font-bold ml-1">vagas ocupadas</span></strong>
                      </div>
                    );
                  })}
                </div>

                {/* Barra de Progresso e Totais */}
                <div className="mb-10">
                  <div className="flex justify-between items-end mb-4">
                    <div>
                      <span className="block text-[10px] uppercase text-muted font-black tracking-widest mb-1">Total</span>
                      <strong className="text-2xl font-black text-white">{totalOccupied} / {totalVacancies} <span className="text-sm text-muted font-bold ml-1">vagas ocupadas</span></strong>
                    </div>
                    <div className="text-right">
                      <span className="block text-[10px] uppercase text-muted font-black tracking-widest mb-1">Vagas Restantes</span>
                      <strong className={`text-xl font-black ${remainingVacancies <= 5 ? 'text-yellow' : 'text-green'}`}>{remainingVacancies}</strong>
                    </div>
                  </div>
                  <div className="h-4 bg-[#070d19] rounded-full overflow-hidden border border-[#1d2a42] p-1 shadow-inner">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${occupancyRate}%` }}
                      className={`h-full rounded-full shadow-lg ${isFull ? 'bg-red' : remainingVacancies <= 5 ? 'bg-yellow' : 'bg-purple'}`}
                    />
                  </div>
                </div>

                {/* Ações */}
                <div className="mt-auto">
                  {isStudentInThisCourse ? (
                    <div className="space-y-4">
                      <div className="w-full py-5 rounded-[1.5rem] bg-purple/10 border border-purple/30 flex flex-col items-center justify-center gap-1 text-purple font-black text-sm uppercase tracking-widest shadow-inner">
                        <div className="flex items-center gap-2">
                          <CheckCircle size={20} /> ✓ Você está matriculado neste curso
                        </div>
                        <div className="flex gap-4 mt-2 text-[10px] opacity-80">
                          <span>TURMA: {userEnrollment.turma}</span>
                          <span>STATUS: {userEnrollment.status || 'Ativo'}</span>
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline"
                        className="w-full py-4 rounded-[1.2rem] border-red/30 text-red hover:bg-red/10 font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2"
                        onClick={handleCancelEnrollment}
                        disabled={isCancelling}
                      >
                        <Trash2 size={14} /> Cancelar Matrícula Atual
                      </Button>
                    </div>
                  ) : userEnrollment ? (
                    <div className="flex flex-col gap-3">
                      <div className="w-full py-5 rounded-[1.5rem] bg-white/5 border border-white/10 flex flex-col items-center justify-center gap-1 text-muted font-black text-sm uppercase tracking-widest opacity-60">
                        <span className="flex items-center gap-2"><XCircle size={18} /> Matrícula indisponível</span>
                        <span className="text-[10px] font-bold lowercase tracking-normal">
                          Você já possui matrícula ativa em{' '}
                          {Array.isArray(userEnrollment?.courses) && userEnrollment.courses.length > 0
                            ? userEnrollment.courses.join(', ')
                            : (userEnrollment?.course || userEnrollment?.courseName)}.
                        </span>
                      </div>
                    </div>
                  ) : isFull ? (
                    <div className="w-full py-5 rounded-[1.5rem] bg-red/10 border border-red/20 flex items-center justify-center gap-3 text-red font-black text-sm uppercase tracking-widest">
                      <XCircle size={20} /> Sem Vagas Disponíveis
                    </div>
                  ) : (
                    <Button 
                      className="w-full py-6 rounded-[1.5rem] font-black text-sm uppercase tracking-[0.15em] shadow-xl shadow-purple/20 hover:shadow-purple/40 hover:-translate-y-1 transition-all" 
                      onClick={() => handleEnrollClick(course)}
                    >
                      Realizar Matrícula
                    </Button>
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>

      {/* Enrollment Form Modal */}
      <AnimatePresence>
        {showForm && (
          <div className="modalOverlay p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              className="modal max-w-xl w-full p-10 bg-[#0a1120] border border-[#1d2a42] rounded-[3rem] shadow-2xl shadow-black max-h-[90vh] overflow-y-auto custom-scrollbar"
            >
              <div className="flex justify-between items-start mb-10">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-purple">
                    <CheckCircle size={18} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Fluxo de Matrícula</span>
                  </div>
                  <h3 className="text-3xl font-black uppercase tracking-tighter">Confirmar Ingresso</h3>
                  <p className="text-muted font-medium">Você está se matriculando em <strong className="text-white">{selectedCourse?.name || "Curso"}</strong></p>
                </div>
                <button onClick={() => setShowForm(false)} className="p-2 text-muted hover:text-white hover:bg-white/5 rounded-xl transition-all">
                  <XCircle size={28}/>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-5">
                  <Input 
                    label="Nome Completo do Candidato"
                    icon={User}
                    required
                    placeholder="Ex: João Silva da Costa"
                    value={formData.fullName}
                    onChange={e => setFormData({...formData, fullName: e.target.value})}
                    className="bg-[#070d19] border-[#1d2a42] h-14"
                  />
                  <Input 
                    label="Documento de Identificação (ID)"
                    icon={Fingerprint}
                    required
                    placeholder="Digite seu identificador único"
                    value={formData.citizenId}
                    onChange={e => setFormData({...formData, citizenId: e.target.value})}
                    className="bg-[#070d19] border-[#1d2a42] h-14"
                  />
                  <Input 
                    label="WhatsApp / Telefone de Contato"
                    icon={Phone}
                    required
                    placeholder="(00) 00000-0000"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="bg-[#070d19] border-[#1d2a42] h-14"
                  />
                  <Input 
                    label="Foto de Perfil (URL Opcional)"
                    icon={Camera}
                    placeholder="https://images.fivemanage.com/..."
                    value={formData.profilePicture}
                    onChange={e => setFormData({...formData, profilePicture: e.target.value})}
                    className="bg-[#070d19] border-[#1d2a42] h-14"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input 
                    label="Criar Senha de Acesso"
                    icon={Lock}
                    type="password"
                    required
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    className="bg-[#070d19] border-[#1d2a42] h-14"
                  />
                  <Input 
                    label="Confirmar Senha"
                    icon={Shield}
                    type="password"
                    required
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                    className="bg-[#070d19] border-[#1d2a42] h-14"
                  />
                </div>

                <div className="bg-purple/5 border border-purple/10 p-6 rounded-[2rem] space-y-3">
                  <div className="flex items-center gap-3 text-purple">
                    <Info size={20} />
                    <strong className="text-sm font-black uppercase tracking-widest">Informações Importantes</strong>
                  </div>
                  <p className="text-xs text-muted leading-relaxed font-medium">
                    Ao confirmar a matrícula, sua turma será definida automaticamente entre <strong className="text-white">A ou B</strong>. Seus dados de acesso serão gerados imediatamente.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button variant="outline" className="flex-1 h-14 rounded-2xl font-black uppercase tracking-widest" type="button" onClick={() => setShowForm(false)}>Cancelar</Button>
                  <Button className="flex-2 h-14 rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-purple/20" type="submit">Confirmar e Gerar Acesso</Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {enrollmentResult && (
          <div className="modalOverlay p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              className="modal max-w-xl w-full p-12 bg-[#0a1120] border border-[#1d2a42] rounded-[3rem] shadow-2xl text-center max-h-[90vh] overflow-y-auto custom-scrollbar"
            >
              <div className="w-24 h-24 bg-green/10 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-inner border border-green/20">
                <CheckCircle size={48} className="text-green" />
              </div>
              <h3 className="text-4xl font-black tracking-tighter uppercase mb-2">Matrícula Confirmada!</h3>
              <p className="text-muted text-lg font-medium mb-10">Bem-vindo à Universidade Lumenfall. Seus dados acadêmicos foram processados.</p>
              
              <div className="bg-[#070d19] border border-[#1d2a42] rounded-[2.5rem] p-8 text-left space-y-5 mb-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple/5 blur-[40px] rounded-full -mr-16 -mt-16" />
                
                <div className="flex items-center gap-6 border-b border-[#1d2a42] pb-6 mb-2">
                  <div className="w-20 h-20 rounded-2xl border-2 border-[#1d2a42] bg-[#0a1120] overflow-hidden shrink-0 shadow-lg">
                    {enrollmentResult.profilePicture ? (
                      <img src={enrollmentResult.profilePicture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl font-black text-white bg-gradient-to-br from-purple/20 to-purple/5">
                        {(enrollmentResult.fullName || "A")[0]}
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-muted text-[10px] font-black uppercase tracking-widest block mb-1">Candidato Matriculado</span>
                    <strong className="text-xl font-black text-white">{enrollmentResult.fullName}</strong>
                  </div>
                </div>

                <div className="flex justify-between items-center border-b border-[#1d2a42] pb-4">
                  <span className="text-muted text-xs font-black uppercase tracking-widest">Matrícula Oficial</span>
                  <strong className="text-lg font-mono text-purple">{enrollmentResult.enrollmentId}</strong>
                </div>
                <div className="flex justify-between items-center border-b border-[#1d2a42] pb-4">
                  <span className="text-muted text-xs font-black uppercase tracking-widest">Curso{Array.isArray(enrollmentResult?.courses) && enrollmentResult.courses.length > 1 ? 's' : ''} Alocado{Array.isArray(enrollmentResult?.courses) && enrollmentResult.courses.length > 1 ? 's' : ''}</span>
                  <strong className="text-lg font-black text-right max-w-[220px]">
                    {Array.isArray(enrollmentResult?.courses) && enrollmentResult.courses.length > 0
                      ? enrollmentResult.courses.join(', ')
                      : (enrollmentResult?.course || enrollmentResult?.courseName)}
                  </strong>
                </div>
                <div className="flex justify-between items-center border-b border-[#1d2a42] pb-4">
                  <span className="text-muted text-xs font-black uppercase tracking-widest">Turma Designada</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="purple" className="px-4 py-1.5 rounded-xl font-black">TURMA {enrollmentResult.turma}</Badge>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted text-xs font-black uppercase tracking-widest">Situação Acadêmica</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green animate-pulse" />
                    <strong className="text-green text-sm font-black uppercase tracking-widest">Ativo</strong>
                  </div>
                </div>
              </div>

              <div className="mb-10 p-8 bg-[#5865F2] rounded-[2.5rem] text-white text-left relative overflow-hidden group shadow-xl shadow-[#5865F2]/20">
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <MessageCircle size={20} />
                    </div>
                    <span className="font-black text-xs uppercase tracking-[0.2em]">Canal Oficial</span>
                  </div>
                  <h4 className="text-2xl font-black tracking-tight mb-2">Ingressar no Discord</h4>
                  <p className="text-sm opacity-90 leading-relaxed font-medium">
                    Horários, comunicados e interações acadêmicas ocorrem exclusivamente através do nosso Discord oficial.
                  </p>
                </div>
                <MessageCircle size={160} className="absolute -right-12 -bottom-12 opacity-10 group-hover:scale-110 transition-transform duration-700" />
              </div>

              <Button className="w-full h-16 rounded-2xl font-black text-lg uppercase tracking-widest shadow-xl shadow-purple/20" onClick={() => setEnrollmentResult(null)}>
                Acessar Portal do Aluno
              </Button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
